import { useEffect, useRef, useState } from 'react';
import { Copy, Download, KeyRound, ShieldAlert } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { useEncryption } from '@/context/EncryptionContext';
import { saveRecoveryPdf } from '@/utils/recovery-artifact';
import { toast } from '@/components/ui/use-toast';
import EncryptionScreen from './EncryptionScreen';

const isNative = Capacitor.isNativePlatform();

const RecoveryKeySetup = () => {
  const { recoveryKey, confirmRecoverySaved } = useEncryption();
  const [confirmed, setConfirmed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const autoDownloaded = useRef(false);

  // Auto-download the recovery PDF (with QR) once on web. On native we don't
  // auto-trigger, since that would pop the share sheet the moment the screen
  // mounts — the user taps "Save" instead.
  useEffect(() => {
    if (isNative || !recoveryKey || autoDownloaded.current) return;
    autoDownloaded.current = true;
    saveRecoveryPdf(recoveryKey).catch((error) => {
      console.error('Recovery PDF download failed:', error);
    });
  }, [recoveryKey]);

  if (!recoveryKey) return null;

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(recoveryKey);
      toast({ title: 'Recovery key copied' });
    } catch {
      toast({ title: 'Could not copy', variant: 'destructive' });
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      await saveRecoveryPdf(recoveryKey);
    } catch (error) {
      console.error('Recovery PDF save failed:', error);
      toast({ title: isNative ? 'Save failed' : 'Download failed', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <EncryptionScreen
      icon={<KeyRound className="h-8 w-8 text-primary mb-2" />}
      title="Save your recovery key"
    >
      <p className="text-sm text-muted-foreground">
        This is the only way to read your entries on a new device or if you
        clear your browser. We can't see it and we can't reset it.
      </p>

      <div className="relative rounded-lg border bg-muted/40 p-4 pr-12 font-mono text-sm tracking-wide break-all">
        {recoveryKey}
        <button
          type="button"
          onClick={copyKey}
          aria-label="Copy recovery key"
          className="absolute right-2 top-2 rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>

      <Button variant="outline" className="w-full gap-2" onClick={download} disabled={downloading}>
        <Download className="h-4 w-4" />
        {downloading ? 'Saving…' : isNative ? 'Save' : 'Download'}
      </Button>

      <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-muted-foreground">
        <ShieldAlert className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
        <span>
          {isNative
            ? 'Tap Save to store a PDF with this key and a QR code. Keep it somewhere safe — anyone who has it can read your data.'
            : 'We saved a PDF with this key and a QR code to your device. Keep it somewhere safe — anyone who has it can read your data.'}
        </span>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="h-4 w-4"
        />
        I've saved my recovery key somewhere safe
      </label>

      <Button className="w-full" disabled={!confirmed} onClick={confirmRecoverySaved}>
        Continue
      </Button>
    </EncryptionScreen>
  );
};

export default RecoveryKeySetup;
